import { useCallback, useEffect, useState } from 'react';
import type { PCInfo } from '../api';
import { PAID_FEATURE_HINT, isPaidTier } from '../planTier';
import type { PlanTier } from '../protocol';
import { loadDirHandle } from '../sync/localDirStore';
import type { SyncMode } from '../sync/syncEngine';
import {
  loadSyncProfiles,
  saveSyncProfiles,
  syncTypeLabel,
  type SyncProfile,
} from '../sync/syncSettings';

export interface SyncJobsPanelProps {
  userId: string;
  pcs: PCInfo[];
  livePlanTier?: PlanTier | null;
  /** Respects job sync type (upload-only vs two-way). */
  onRunJob: (profile: SyncProfile, mode: SyncMode) => Promise<void>;
  /** Always a full pull of the host destination → local folder. */
  onDownload: (profile: SyncProfile) => Promise<void>;
}

function tierForJob(pcs: PCInfo[], profile: SyncProfile): PlanTier | null {
  return pcs.find((p) => p.id === profile.pcId)?.planTier ?? null;
}

export function SyncJobsPanel({
  userId,
  pcs,
  livePlanTier,
  onRunJob,
  onDownload,
}: SyncJobsPanelProps): JSX.Element | null {
  const [profiles, setProfiles] = useState<SyncProfile[]>(() => loadSyncProfiles(userId));
  const [busyId, setBusyId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    setProfiles(loadSyncProfiles(userId));
  }, [userId]);

  const removeProfile = useCallback(
    (id: string) => {
      const next = profiles.filter((p) => p.id !== id);
      setProfiles(next);
      saveSyncProfiles(userId, next);
    },
    [profiles, userId]
  );

  const runJob = useCallback(
    async (profile: SyncProfile) => {
      const tier = tierForJob(pcs, profile) ?? livePlanTier;
      if (!isPaidTier(tier)) {
        setStatus(PAID_FEATURE_HINT);
        return;
      }
      setBusyId(profile.id);
      const verb = profile.syncType === 'two-way' ? 'Syncing' : 'Uploading';
      setStatus(`${verb} ${profile.localDirName} ↔ ${profile.pcName}…`);
      try {
        const handle = await loadDirHandle(profile.id);
        if (!handle) throw new Error('Local folder access expired — set up sync again from the drive.');
        await onRunJob(profile, 'incremental');
        setStatus(profile.syncType === 'two-way' ? 'Sync complete' : `Uploaded to ${profile.pcName}`);
      } catch (e) {
        setStatus(String(e));
      } finally {
        setBusyId(null);
      }
    },
    [pcs, livePlanTier, onRunJob]
  );

  const runDownload = useCallback(
    async (profile: SyncProfile) => {
      const tier = tierForJob(pcs, profile) ?? livePlanTier;
      if (!isPaidTier(tier)) {
        setStatus(PAID_FEATURE_HINT);
        return;
      }
      setBusyId(profile.id);
      setStatus(`Downloading ${profile.pcName} → ${profile.localDirName}…`);
      try {
        const handle = await loadDirHandle(profile.id);
        if (!handle) throw new Error('Local folder access expired — set up sync again from the drive.');
        await onDownload(profile);
        setStatus(`Downloaded to ${profile.localDirName}`);
      } catch (e) {
        setStatus(String(e));
      } finally {
        setBusyId(null);
      }
    },
    [pcs, livePlanTier, onDownload]
  );

  if (profiles.length === 0) return null;

  return (
    <section className="panel sync-panel sync-jobs-global">
      <h3>Saved sync jobs</h3>
      <p className="muted">
        ↑ Upload or ⟳ Sync runs incremental (per job type). ↓ Download always pulls the full host
        folder to this device.
      </p>
      <ul className="sync-list">
        {profiles.map((p) => {
          const dest = `${p.pcName} · ${p.hostShareName}${p.hostRelDir ? ` / ${p.hostRelDir}` : ''}`;
          const primaryLabel = p.syncType === 'two-way' ? '⟳ Sync' : '↑ Upload';
          return (
            <li key={p.id}>
              <div className="sync-job-desc">
                <strong>{p.localDirName}</strong>
                <span className="sync-job-arrow">↔</span>
                <span>{dest}</span>
                <span className="sync-job-type">{syncTypeLabel(p.syncType)}</span>
              </div>
              <div className="sync-job-actions">
                <button
                  type="button"
                  className="btn ghost"
                  disabled={busyId !== null}
                  onClick={() => void runJob(p)}
                  title={
                    p.syncType === 'two-way'
                      ? 'Merge local and host (both directions)'
                      : 'Upload local changes to host'
                  }
                >
                  {primaryLabel}
                </button>
                <button
                  type="button"
                  className="btn ghost"
                  disabled={busyId !== null}
                  onClick={() => void runDownload(p)}
                  title="Download entire host destination to this device"
                >
                  ↓ Download
                </button>
                <button
                  type="button"
                  className="btn ghost"
                  disabled={busyId !== null}
                  onClick={() => removeProfile(p.id)}
                >
                  Remove
                </button>
              </div>
            </li>
          );
        })}
      </ul>
      {status && <p className="sync-status">{status}</p>}
    </section>
  );
}
